import React, { useEffect, useState } from 'react';
import CenterModalWindow from '../../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import { storageBoxApi } from '../api/storageBoxApi';

const EXPIRY_FOREVER = 'forever';
const EXPIRY_24H = '24h';
const EXPIRY_RANGE = 'range';

function toDateInputValue(d) {
  if (!d) return '';
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  return x.toISOString().slice(0, 10);
}

export default function StorageBoxLockShareModal({ item, lockSessionToken, onClose, onNotify }) {
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [ownerPin, setOwnerPin] = useState('');
  const [expiryPreset, setExpiryPreset] = useState(EXPIRY_24H);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = query.trim();
    if (t.length < 2) {
      setUsers([]);
      return;
    }
    const handle = setTimeout(() => {
      storageBoxApi.searchUsers(t).then((r) => setUsers(r.users || [])).catch(() => setUsers([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  const buildExpiryBody = () => {
    if (expiryPreset === EXPIRY_FOREVER) {
      return { expiryPreset: EXPIRY_FOREVER, neverExpires: true };
    }
    if (expiryPreset === EXPIRY_24H) {
      return { expiryPreset: EXPIRY_24H, expiresInHours: 24 };
    }
    if (!rangeStart || !rangeEnd) {
      return null;
    }
    const startsAt = new Date(`${rangeStart}T00:00:00`);
    const expiresAt = new Date(`${rangeEnd}T23:59:59`);
    return { expiryPreset: EXPIRY_RANGE, startsAt: startsAt.toISOString(), expiresAt: expiresAt.toISOString() };
  };

  const handleCreate = async () => {
    if (!selectedUser) {
      onNotify?.('Choose who should receive this secure share');
      return;
    }
    if (!ownerPin.trim()) {
      onNotify?.('Enter your Lock folder password to authorize sharing');
      return;
    }
    const expiryBody = buildExpiryBody();
    if (!expiryBody) {
      onNotify?.('Choose a start and end date for access');
      return;
    }
    setBusy(true);
    try {
      await storageBoxApi.lockCreateShare(
        {
          itemId: item.itemId,
          granteeUserId: selectedUser.userId,
          pin: ownerPin,
          ...expiryBody,
        },
        lockSessionToken
      );
      setDone(true);
      window.dispatchEvent(new CustomEvent('storagebox-shared-list-changed'));
      onNotify?.(`Secure share sent to ${selectedUser.displayName || selectedUser.email}`);
    } catch (e) {
      onNotify?.(e.message || 'Share failed');
    } finally {
      setBusy(false);
    }
  };

  const recipientLabel =
    selectedUser
      ? [selectedUser.first_name, selectedUser.last_name].filter(Boolean).join(' ') ||
        selectedUser.displayName ||
        selectedUser.email
      : null;

  return (
    <CenterModalWindow
      isOpen={Boolean(item)}
      onClose={onClose}
      width="min(520px, calc(100vw - 32px))"
      height="auto"
      contentStyle={{ height: 'auto', maxHeight: 'min(85vh, 720px)' }}
      contentClassName="sb-share-modal"
    >
      <div className="sb-share-modal__inner" data-modal-scroll-port>
        <header className="sb-share-modal__head">
          <h2 id="sb-lock-share-title">Secure share “{item?.name}”</h2>
        </header>
        <div className="sb-share-modal__body">
          <p className="sb-hint">
            Enter your Lock folder password, then pick someone in your organization. They will find this under{' '}
            <strong>Shared</strong> in their sidebar—grouped under your name—and can open it while access is
            valid. No separate one-time PIN is generated.
          </p>

          <label className="sb-field">
            <span>Your Lock folder password</span>
            <input
              type="password"
              value={ownerPin}
              onChange={(e) => setOwnerPin(e.target.value)}
              autoComplete="current-password"
              disabled={busy || done}
            />
          </label>

          <fieldset className="sb-share-expiry-fieldset">
            <legend className="sb-field__legend">Access duration</legend>
            <div className="sb-share-expiry-options">
              <label className="sb-field sb-field--radio">
                <input
                  type="radio"
                  name="sb-lock-share-expiry"
                  checked={expiryPreset === EXPIRY_24H}
                  onChange={() => setExpiryPreset(EXPIRY_24H)}
                  disabled={busy || done}
                />
                <span>24 hours</span>
              </label>
              <label className="sb-field sb-field--radio">
                <input
                  type="radio"
                  name="sb-lock-share-expiry"
                  checked={expiryPreset === EXPIRY_FOREVER}
                  onChange={() => setExpiryPreset(EXPIRY_FOREVER)}
                  disabled={busy || done}
                />
                <span>Forever</span>
              </label>
              <label className="sb-field sb-field--radio">
                <input
                  type="radio"
                  name="sb-lock-share-expiry"
                  checked={expiryPreset === EXPIRY_RANGE}
                  onChange={() => setExpiryPreset(EXPIRY_RANGE)}
                  disabled={busy || done}
                />
                <span>Date range</span>
              </label>
            </div>
            {expiryPreset === EXPIRY_RANGE ? (
              <div className="sb-share-expiry-range">
                <label className="sb-field">
                  <span>From</span>
                  <input
                    type="date"
                    value={rangeStart}
                    min={toDateInputValue(new Date())}
                    onChange={(e) => setRangeStart(e.target.value)}
                    disabled={busy || done}
                  />
                </label>
                <label className="sb-field">
                  <span>Until</span>
                  <input
                    type="date"
                    value={rangeEnd}
                    min={rangeStart || toDateInputValue(new Date())}
                    onChange={(e) => setRangeEnd(e.target.value)}
                    disabled={busy || done}
                  />
                </label>
              </div>
            ) : null}
          </fieldset>

          <label className="sb-field">
            <span>Share with</span>
            <input
              type="search"
              placeholder="Search by name or email"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={busy || done}
            />
          </label>
          <ul className="sb-user-pick-list">
            {users.map((u) => (
              <li key={u.userId}>
                <button
                  type="button"
                  className={selectedUser?.userId === u.userId ? 'sb-user-pick sb-user-pick--active' : 'sb-user-pick'}
                  onClick={() => setSelectedUser(u)}
                  disabled={busy || done}
                >
                  <span>{u.displayName || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}</span>
                  <small>{u.email}</small>
                </button>
              </li>
            ))}
          </ul>

          {done && recipientLabel ? (
            <p className="sb-hint sb-share-success-hint">
              {recipientLabel} will see “{item?.name}” under <strong>Shared</strong>, inside the folder with your
              name.
            </p>
          ) : null}
        </div>
        <footer className="sb-share-modal__foot">
          <button type="button" className="sb-secondary-btn" onClick={onClose}>
            {done ? 'Done' : 'Cancel'}
          </button>
          {!done ? (
            <button
              type="button"
              className="sb-primary-btn"
              onClick={handleCreate}
              disabled={busy || !selectedUser}
            >
              {busy ? 'Sharing…' : 'Share securely'}
            </button>
          ) : null}
        </footer>
      </div>
    </CenterModalWindow>
  );
}
